import axios from "axios";
import store from "@/store";
import router from "@/router";

// Chaves de sessão do usuário admin (limpas quando o userToken expira)
const ADMIN_SESSION_KEYS = [
  "userToken",
  "currentUser",
  "pointOfSales",
  "currentPointOfSale",
];

function clearAdminSessionAndRedirect() {
  ADMIN_SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key));
  // Vai para "/" (HomeView → login.vue), NÃO para "/user-auth": aquela tela é
  // legada e não consegue autenticar (chama userAuthenticate(username, password)
  // quando a assinatura é (store, email, password), e o handleSubmit lança erro
  // justamente quando o formulário está válido). Com o guard de rota, mandar o
  // usuário para lá o deixaria preso: não loga e não acessa nenhum /admin/*.
  if (router.currentRoute.value?.path !== "/") {
    router.push("/");
  }
}

// Configuração base do axios
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_MENUUS_API_URL,
  timeout: 30000,
});

// Interceptador de requisição - adiciona os tokens automaticamente
apiClient.interceptors.request.use(
  (config) => {
    const token = store.getters["auth/getToken"];
    if (token) {
      config.headers["AuthorizationIntegrator"] = token;
    }

    // Token do usuário admin (endpoints que exigem AuthorizationUser).
    // Injetado automaticamente quando existe; o backend só lê onde precisa.
    const userToken = sessionStorage.getItem("userToken");
    if (userToken) {
      config.headers["AuthorizationUser"] = userToken;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Interceptador de resposta - trata erros 401 automaticamente
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (!error.response || error.response.status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    // Primeira 401: tenta renovar o token integrador e repetir a requisição.
    if (!originalRequest._retry) {
      originalRequest._retry = true;

      try {
        await store.dispatch("auth/refreshToken");
        const newToken = store.getters["auth/getToken"];
        originalRequest.headers["AuthorizationIntegrator"] = newToken;
        // Tenta novamente a requisição original
        return apiClient(originalRequest);
      } catch (refreshError) {
        console.error("Erro ao renovar token:", refreshError);
        return Promise.reject(refreshError);
      }
    }

    // 401 persistiu após renovar o integrador. Se havia sessão de usuário
    // admin, tratamos como userToken expirado (não há endpoint de refresh):
    // limpa a sessão admin e manda para o login.
    if (sessionStorage.getItem("userToken")) {
      clearAdminSessionAndRedirect();
    }

    return Promise.reject(error);
  },
);

export default apiClient;
